import React, { useState } from 'react';
import { Clock, MapPin, Video, Users } from 'lucide-react';
import { classSchedule } from '../data/mock';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

export const Classes = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    level: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    toast({
      title: "Enquiry Submitted!",
      description: "Thank you for your interest. We will contact you soon.",
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      level: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-ivory to-cream py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl md:text-6xl font-bold text-maroon mb-4">Classes & Schedule</h1>
          <p className="text-lg text-maroon-dark max-w-2xl mx-auto">
            Learn the sacred art of Bharatanatyam with structured training for all levels
          </p>
          <div className="w-24 h-1 bg-gold mx-auto mt-6"></div>
        </div>

        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-16 bg-white rounded-lg shadow-md p-8 fade-in">
          <h2 className="text-2xl font-bold text-maroon mb-4">Why Learn Bharatanatyam?</h2>
          <p className="text-maroon-dark leading-relaxed mb-4">
            Bharatanatyam is one of the oldest classical dance forms of India, originating from the temples of Tamil Nadu. 
            It combines intricate footwork (nritta), expressive storytelling (abhinaya), and devotional themes to create 
            a complete art form that nurtures both body and spirit.
          </p>
          <p className="text-maroon-dark leading-relaxed">
            Our classes emphasize traditional training methods while making the art accessible to students of all ages 
            and backgrounds. Whether you're a beginner or an advanced student preparing for Arangetram, we offer 
            personalized guidance in a nurturing environment.
          </p>
        </div>

        {/* Class Video */}
        <div className="max-w-md mx-auto mb-16 fade-in" data-testid="class-video-section">
          <h2 className="text-3xl font-bold text-maroon mb-8 text-center">See Our Classes in Action</h2>
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="relative pb-[177.78%] bg-maroon-light">
              <iframe
                src="https://www.youtube.com/embed/M4dEtY_N2MM"
                title="Nrityavilasini Bharatanatyam classes"
                className="absolute top-0 left-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="p-4 text-center">
              <p className="text-maroon font-semibold" data-testid="class-video-title">Nrityavilasini Bharatanatyam Classes</p>
            </div>
          </div>
        </div>

        {/* Class Levels */}
        <div className="max-w-6xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-maroon mb-8 text-center">Class Levels</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {Object.values(classSchedule).map((classInfo, index) => (
              <div 
                key={classInfo.level} 
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="bg-maroon text-ivory p-6">
                  <h3 className="text-2xl font-bold mb-2">{classInfo.level}</h3>
                  <div className="flex items-center gap-2 text-gold">
                    <Clock size={16} />
                    <span className="text-sm">{classInfo.duration}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-maroon-dark mb-6 leading-relaxed">
                    {classInfo.description}
                  </p>
                  
                  <h4 className="font-semibold text-maroon mb-3">Schedule:</h4>
                  <div className="space-y-3">
                    {classInfo.schedule.map((session, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <div className="w-2 h-2 bg-gold rounded-full mt-1.5 flex-shrink-0"></div>
                        <div>
                          <p className="font-semibold text-maroon">{session.day}</p>
                          <p className="text-maroon-dark">{session.time}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {session.mode === 'Online' ? (
                              <Video size={12} className="text-maroon" />
                            ) : (
                              <MapPin size={12} className="text-maroon" />
                            )}
                            <span className="text-xs text-maroon-dark">{session.mode}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-maroon mb-8 text-center">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4 bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-maroon mb-2">Traditional Adavus</h3>
                <p className="text-maroon-dark text-sm">
                  Master the fundamental steps and movements that form the foundation of Bharatanatyam
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-maroon mb-2">Expressive Abhinaya</h3>
                <p className="text-maroon-dark text-sm">
                  Develop facial expressions, hand gestures (mudras), and storytelling techniques
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-maroon mb-2">Classical Repertoire</h3>
                <p className="text-maroon-dark text-sm">
                  Learn traditional pieces including alarippu, jatiswaram, varnam, and tillana
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-maroon rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-maroon mb-2">Theory & History</h3>
                <p className="text-maroon-dark text-sm">
                  Understand the cultural context, terminology, and rich heritage of the art form
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Registration CTA */}
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-maroon to-maroon-dark text-ivory rounded-lg shadow-xl p-12 fade-in">
          <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Journey?</h2>
          <p className="text-ivory/90 mb-8 text-lg">
            Join our classes and experience the transformative power of Bharatanatyam
          </p>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-gold text-maroon hover:bg-gold/90 px-8 py-6 text-lg font-semibold">
                Register / Enquire Now
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-ivory">
              <DialogHeader>
                <DialogTitle className="text-2xl text-maroon">Class Enquiry Form</DialogTitle>
                <DialogDescription className="text-maroon-dark">
                  Fill in your details and we'll get back to you soon.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name" className="text-maroon">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-maroon">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-maroon">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="level" className="text-maroon">Preferred Level</Label>
                  <select
                    id="level"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    required
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-maroon"
                  >
                    <option value="">Select a level</option>
                    <option value="Beginner">Beginner</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="message" className="text-maroon">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full bg-maroon text-ivory hover:bg-maroon/90">
                  Submit Enquiry
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
